import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import {
  Fragment,
  useState,
  useEffect,
  useLayoutEffect,
} from "react";
import { usePrivy } from "@privy-io/react-auth";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);
import { FloatingLeftButton } from "@/components/FloatingLeftButton";

import ResourceBar from "@/components/ResourceBar";
import { ScrollArea } from "@/components/ui/scroll-area";
import AnimatedLoading from "@/components/AnimatedLoading";
import { cn, generateTextShadow, USDC_ICON } from "@/lib/utils";
import { useShop, useStats } from "@/lib/hooks/useApi";
import type { Shop } from "@/lib/types/api";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { MODEL_REGISTRY, type ModelType } from "@/components/RocketsOverlay";
import { Canvas } from "@react-three/fiber";
import { OrthographicCamera } from "@react-three/drei";
import * as THREE from "three";
import { ItemIcon } from "./-components/ItemIcon";
import {
  translateShopItemName,
  translateShopItemSubtitle,
  translateShopSubItemName,
} from "@/lib/shopTranslations";
import { ShopDialog } from "./-components/ShopDialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { InfoIcon } from "lucide-react";
import { t } from "@/lib/i18n";

export const Route = createFileRoute("/(game)/shop/")({
  component: Shop,
});

function Shop() {
  const navigate = useNavigate();
  const router = useRouter();
  const { user } = usePrivy();
  const walletAddress = user?.wallet?.address;
  const { data: userStats } = useStats(walletAddress ?? "");
  const userId = userStats?.userId;
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Shop | null>(null);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;

    const updateCurrentSlide = () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
    };

    carouselApi.on("select", updateCurrentSlide);
    updateCurrentSlide();

    return () => {
      carouselApi.off("select", updateCurrentSlide);
    };
  }, [carouselApi]);

  const {
    data: shopItems,
    isLoading,
    error,
    refetch: refetchShopList,
  } = useShop({
    userId,
    limit: 100,
  });

  useLayoutEffect(() => {
    if (!carouselApi || !shopItems || !Array.isArray(shopItems)) return;

    carouselApi.reInit();
  }, [carouselApi, shopItems]);

  const handleOpenModal = (item: Shop) => {
    setSelectedItem(item);
    setIsSheetOpen(true);
  };

  if (isLoading) {
    return <AnimatedLoading />;
  }

  if (error || !shopItems || !Array.isArray(shopItems)) {
    return (
      <div className="flex-1 flex items-center justify-center text-zinc-400">
        Failed to load shop items
      </div>
    );
  }

  // Filter items by category
  const specialOffers = shopItems.filter((item) => item.isSpecialOffer);
  const consumables = shopItems.filter(
    (item) => item.category === "consumables",
  );
  const cosmetics = shopItems.filter((item) => item.category === "cosmetics");
  const bundles = shopItems.filter(
    (item) => item.category === "bundle" && !item.isSpecialOffer,
  );

  const renderSpecialOfferGrid = (specialOfferItem: Shop) => {
    if (!specialOfferItem.itemData || specialOfferItem.itemData.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap justify-center gap-1.5 w-full">
        {specialOfferItem.itemData.map((item, index) => {
          const isOwned = "isOwned" in item && item.isOwned === true;
          return (
            <div
              key={item.itemId || index}
              className={cn(
                "relative flex flex-col items-center justify-center shrink-0",
                isOwned && "grayscale brightness-50",
              )}
              style={{
                width: "calc((100% - 0.375rem * 3) / 4)",
                flexBasis: "calc((100% - 0.375rem * 3) / 4)",
                minWidth: "calc((100% - 0.375rem * 3) / 4)",
                maxWidth: "calc((100% - 0.375rem * 3) / 4)",
                minHeight: "80px",
              }}
            >
              {/* Icon - bigger */}
              <div
                className={cn(
                  "relative w-full flex-1 flex items-center justify-center min-h-[60px]",
                )}
              >
                {/* Owned Ribbon */}
                {isOwned && (
                  <div
                    className="absolute -top-2 -right-2 z-20 h-10 w-10"
                    style={{ filter: "none" }}
                  >
                    <img src="/assets/ribbon-green.png" alt="Owned" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-[10px] -mt-3 font-bold leading-none w-full">
                      <span
                        className="text-white"
                        style={{ textShadow: generateTextShadow(1.2) }}
                      >
                        {t("shop.owned")}
                      </span>
                    </div>
                  </div>
                )}
                <svg
                  viewBox="0 0 43 51"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute bottom-0 w-full h-full scale-125 animate-pulse"
                >
                  <path
                    d="M0 5H43L35.4118 41H7.36328L0 5Z"
                    fill="url(#paint0_radial_7894_29)"
                  />
                  <defs>
                    <radialGradient
                      id="paint0_radial_7894_29"
                      cx="0"
                      cy="0"
                      r="1"
                      gradientUnits="userSpaceOnUse"
                      gradientTransform="translate(21.497 41.7345) rotate(-90) scale(36.7345 32.9036)"
                    >
                      <stop stop-color="#FFFC63" />
                      <stop offset="1" stop-color="#98975A" stop-opacity="0" />
                    </radialGradient>
                  </defs>
                </svg>
                <img
                  src="https://cdn.navalabs.io/components/shop/item-podium_2SRqqz.png"
                  alt="Item Podium"
                  className="absolute -bottom-8 w-full"
                  style={{
                    imageRendering: "pixelated",
                  }}
                />
                {Object.keys(MODEL_REGISTRY).includes(item.itemId) ? (
                  (() => {
                    const ModelComponent =
                      MODEL_REGISTRY[item.itemId as ModelType];
                    return (
                      <div className="h-16 w-20 overflow-hidden pointer-events-none xs:h-20 xs:w-24">
                        <Canvas
                          style={{
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                          }}
                        >
                          <OrthographicCamera
                            makeDefault
                            position={[0, 0, 1000]}
                            zoom={1}
                          />
                          <ambientLight position={[0, 0, 10]} intensity={3} />
                          <directionalLight
                            position={[0, THREE.MathUtils.degToRad(40), 0]}
                            intensity={12}
                          />
                          <ModelComponent rotation={0} />
                        </Canvas>
                      </div>
                    );
                  })()
                ) : (
                  <ItemIcon
                    className="w-16 h-auto object-cover xs:w-20 z-1"
                    item={item}
                    userProfilePicture={userStats?.profilePicture}
                  />
                )}
              </div>

              {/* Name - floating below icon */}
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center">
                <span
                  className="text-white text-xs font-black uppercase text-center wrap-break-word"
                  style={{ textShadow: generateTextShadow(1.25) }}
                >
                  {translateShopSubItemName(item.itemName, item.itemId)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderItemGrid = (items: Shop[]) => (
    <div className="grid grid-cols-3 gap-3 w-full">
      {items.map((item) => {
        const isCosmetic = item.category === "cosmetics";
        const isBundle = item.category === "bundle";
        const isOwned = item.isOwned;
        const isDisabled = (isCosmetic || isBundle) && isOwned;
        const isItemFree = item.priceInUsdc === 0;

        return (
          <button
            key={item.id}
            onClick={() => !isDisabled && handleOpenModal(item)}
            disabled={isDisabled}
            className={cn(
              "dark-blue-elevated-container relative flex flex-col items-center h-36 xs:h-44 py-2",
              isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
              isItemFree && "brightness-110",
            )}
          >
            {/* Discount Ribbon */}
            {item.discount &&
            item.discount.percentage > 0 &&
            item.priceBeforeDiscount > item.priceInUsdc ? (
              <div className="absolute -top-4 -right-2 z-30 h-10 w-10">
                <img src="/assets/discount-tag.png" alt="Discount" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-xs font-bold leading-none w-full gap-0 ml-0.5">
                  <span className="text-white">
                    {item.discount.percentage.toFixed(0)}%
                  </span>
                  <span className="text-white">OFF</span>
                </div>
              </div>
            ) : null}
            {/* Icon - centered */}
            <div className="relative w-full flex-1 flex items-center justify-center">
              {Object.keys(MODEL_REGISTRY).includes(item.id) ? (
                (() => {
                  const ModelComponent = MODEL_REGISTRY[item.id as ModelType];
                  return (
                    <div className="h-16 w-20 overflow-hidden pointer-events-none">
                      <Canvas
                        style={{
                          width: "100%",
                          height: "100%",
                          pointerEvents: "none",
                        }}
                      >
                        <OrthographicCamera
                          makeDefault
                          position={[0, 0, 1000]}
                          zoom={1}
                        />
                        <ambientLight position={[0, 0, 10]} intensity={3} />
                        <directionalLight
                          position={[0, THREE.MathUtils.degToRad(40), 0]}
                          intensity={12}
                        />
                        <ModelComponent rotation={0} />
                      </Canvas>
                    </div>
                  );
                })()
              ) : (
                <ItemIcon
                  className={cn(
                    item.name === "Trophy" ? "w-12" : "w-16",
                    " h-auto object-cover",
                  )}
                  item={item}
                  userProfilePicture={userStats?.profilePicture}
                />
              )}
            </div>

            {/* Name, Subtitle, and Price - at bottom */}
            <div className="w-full flex flex-col items-center justify-center gap-0.5 px-2">
              <span
                className="text-white text-xs font-black uppercase text-center line-clamp-2 wrap-break-word"
                style={{ textShadow: generateTextShadow(1.25) }}
              >
                {translateShopItemName(item.name, item.shopId)}
              </span>
              <span className="text-white/70 text-[10px] text-center leading-tight line-clamp-1 wrap-break-word">
                {translateShopItemSubtitle(item.subtitle, item.shopId)}
              </span>
              {isDisabled ? (
                <div className="w-full flex items-center justify-center text-white font-bold text-center leading-none mt-0.5">
                  <span
                    className="text-xs xs:text-sm"
                    style={{ textShadow: generateTextShadow(1) }}
                  >
                    {t("shop.purchased")}
                  </span>
                </div>
              ) : (
                <div className="w-full flex items-center justify-center gap-1.5 text-white font-bold text-center leading-none mt-0.5">
                  {item.priceBeforeDiscount &&
                  item.priceBeforeDiscount > item.priceInUsdc ? (
                    <>
                      <div className="flex gap-0.5 items-center text-white/60 text-xs line-through">
                        <img
                          src={USDC_ICON}
                          alt="USD Icon"
                          className="w-3 h-3"
                        />
                        <span>{item.priceBeforeDiscount}</span>
                      </div>
                      <div className="flex gap-0.5 items-center">
                        <img
                          src={USDC_ICON}
                          alt="USD Icon"
                          className="w-4 h-4"
                        />
                        <span
                          className="text-sm xs:text-md"
                          style={{ textShadow: generateTextShadow(1.25) }}
                        >
                          {item.priceInUsdc}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex gap-1 items-center">
                      {!isItemFree && (
                        <img
                          src={USDC_ICON}
                          alt="USD Icon"
                          className="w-4 h-4"
                        />
                      )}
                      <span
                        className="text-sm xs:text-md"
                        style={{ textShadow: generateTextShadow(1.25) }}
                      >
                        {isItemFree ? t("shop.free") : item.priceInUsdc}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );

  return (
    <Fragment>
      <div className="relative flex flex-col h-full bg-[#1D2A52] pb-12">
        {/* Top section with background */}
        <div
          className="relative w-full px-2"
          style={{
            backgroundImage:
              "url(https://cdn.navalabs.io/components/shop/shop-tent_i48f33.png)",
            backgroundRepeat: "repeat-x",
            backgroundSize: "auto 100%",
            imageRendering: "pixelated",
          }}
        >
          <FloatingLeftButton
            // onClick={() => navigate({ to: "/home" })}
            onClick={() => router.history.back()}
            className="absolute left-0 top-4 z-50"
            icon={
              <svg
                className="w-4 h-4"
                viewBox="0 0 9 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                  fill="white"
                />
              </svg>
            }
          />
          <ResourceBar
            className="absolute top-4.5 left-14 flex z-10 pl-3 space-x-4"
            showMenu={false}
            showBadge={false}
          />

          <button
            onClick={() => navigate({ to: "/inventory" })}
            className="dark-blue-elevated-container absolute right-4 top-3.5 z-50 h-9 flex items-center justify-between"
            style={{
              borderWidth: "2px 2px 2px 2px",
            }}
          >
            <img
              src={`https://cdn.navalabs.io/icons/bag_S5thrx.png`}
              alt={"Inventory Icon"}
              className="size-6 mr-1 -mt-1"
            />
            <span
              className="uppercase text-white text-xs font-black"
              style={{
                textShadow: generateTextShadow(1),
              }}
            >
              {t("shop.inventory")}
            </span>
            <div></div>
          </button>

          {/* Header */}
          <div className="w-full h-[62px] flex flex-col items-center justify-center mt-12">
            <span
              className="uppercase text-white font-black text-2xl text-center"
              style={{
                textShadow: generateTextShadow(1.5),
              }}
            >
              {t("shop.shopTitle")}
            </span>
          </div>
        </div>

        <div className="flex flex-col w-full flex-1 min-h-0 px-2 overflow-hidden">
          {/* Shop Sections */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <ScrollArea
              className="flex-1 min-h-0 [&_*[data-slot='scroll-area-scrollbar']]:hidden"
              type="auto"
            >
              <div className="flex flex-col gap-6 p-1 overflow-hidden">
                {/* Special Offer Section */}
                {specialOffers.length > 0 && (
                  <div className="flex flex-col gap-2 w-full min-w-0 relative overflow-visible">
                    <Carousel
                      setApi={setCarouselApi}
                      className="w-full min-w-0 overflow-hidden"
                      opts={{
                        loop: true,
                      }}
                      plugins={[
                        Autoplay({
                          delay: 5_000,
                        }),
                      ]}
                    >
                      <CarouselContent className="-ml-2 w-auto gap-0">
                        {specialOffers.map((specialOffer) => (
                          <CarouselItem
                            key={specialOffer.id}
                            className={cn(
                              "basis-full pl-2 min-w-0 overflow-hidden",
                            )}
                          >
                            <button
                              onClick={() => handleOpenModal(specialOffer)}
                              className="infobanner flex flex-col gap-0 relative cursor-pointer hover:opacity-90 transition-opacity w-full min-w-0 overflow-visible"
                            >
                              <div
                                className="w-full p-2 border-b-0 flex flex-col min-h-100"
                                style={{
                                  backgroundImage:
                                    "url(https://cdn.navalabs.io/components/shop/premium-offer_A3rLFG.png)",
                                  backgroundSize: "100% 100%",
                                  backgroundPosition: "center",
                                  backgroundRepeat: "no-repeat",
                                  imageRendering: "pixelated",
                                }}
                              >
                                {/* Title */}
                                <div className="flex flex-col items-center justify-center relative">
                                  <div className="flex flex-col justify-center items-center">
                                    <div className="drop-shadow-[0_0px_4px_#fdc700] text-yellow-400 mt-4">
                                      <span className=" text-white font-black text-3xl shine-md relative leading-5 uppercase">
                                        {(() => {
                                          const translated =
                                            translateShopItemName(
                                              specialOffer.name,
                                              specialOffer.shopId,
                                            );
                                          const nameParts =
                                            translated.split(" ");
                                          const firstPart = nameParts[0] || "";
                                          const secondPart = nameParts
                                            .slice(1)
                                            .join(" ");
                                          return (
                                            <>
                                              {firstPart}
                                              <br></br>
                                              {secondPart
                                                ? `${secondPart} `
                                                : ""}
                                              {t("shop.offer")}
                                            </>
                                          );
                                        })()}
                                      </span>
                                    </div>

                                    {specialOffer.specialOfferEndDate > 0 ? (
                                      <div className="flex  items-center -pl-4 pr-1 mt-1.5 gap-2">
                                        <img
                                          src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
                                          className="w-4"
                                          alt="Timer"
                                        />
                                        <p className="font-extrabold text-white text-sm tracking-wider">
                                          {(() => {
                                            const target =
                                              specialOffer.specialOfferEndDate;
                                            const now = dayjs().unix();
                                            const dur = dayjs.duration(
                                              target - now,
                                              "seconds",
                                            );
                                            const days = Math.floor(
                                              dur.asDays(),
                                            );
                                            const hours = dur.hours();
                                            return `${days}d ${hours}h`;
                                          })()}
                                        </p>
                                      </div>
                                    ) : null}
                                  </div>

                                  {/* Discount Ribbon - Top Right of Title */}
                                  {specialOffer?.discount &&
                                  specialOffer.discount.percentage > 0 &&
                                  specialOffer.priceBeforeDiscount >
                                    specialOffer.priceInUsdc ? (
                                    <div className="absolute -top-1 right-12 z-30 h-12 w-12 scale-150">
                                      <img
                                        src="https://cdn.navalabs.io/components/shop/discount-tag_eHt2Zm.png"
                                        alt="Discount"
                                      />
                                      <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-sm font-bold leading-none w-full gap-0 ml-0.5">
                                        <span className="text-white">
                                          {specialOffer.discount.percentage.toFixed(
                                            0,
                                          )}
                                          %
                                        </span>
                                        <span className="text-white -mt-0.5">
                                          OFF
                                        </span>
                                      </div>
                                    </div>
                                  ) : null}
                                  {/* Timer - Below Title */}
                                </div>
                                {/* Items - centered */}
                                <div className="flex-1 flex items-center justify-center px-4 -mt-8">
                                  {renderSpecialOfferGrid(specialOffer)}
                                </div>
                                {/* Price at Bottom with Border */}
                                <div className="w-full flex items-center justify-center gap-1.5 text-white font-bold mb-2">
                                  {specialOffer.priceBeforeDiscount &&
                                  specialOffer.priceBeforeDiscount >
                                    specialOffer.priceInUsdc ? (
                                    <>
                                      <div className="flex gap-1 items-center text-white/60 text-lg line-through">
                                        <img
                                          src={USDC_ICON}
                                          alt="USD Icon"
                                          className="w-5 h-5"
                                        />
                                        <span>
                                          {specialOffer.priceBeforeDiscount}
                                        </span>
                                      </div>
                                      <div className="flex gap-1 items-center drop-shadow-[0_0px_6px_#fee685]">
                                        <img
                                          src={USDC_ICON}
                                          alt="USD Icon"
                                          className="w-6 h-6 border border-[#BB7D20] rounded-full"
                                        />
                                        <span
                                          className="text-xl"
                                          style={{
                                            textShadow: generateTextShadow(
                                              1.25,
                                              "#BB7D20",
                                            ),
                                          }}
                                        >
                                          {specialOffer.priceInUsdc}
                                        </span>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="flex gap-1 items-center">
                                      <img
                                        src={USDC_ICON}
                                        alt="USD Icon"
                                        className="w-6 h-6"
                                      />
                                      <span
                                        className="text-xl"
                                        style={{
                                          textShadow: generateTextShadow(1.25),
                                        }}
                                      >
                                        {specialOffer.priceInUsdc}
                                      </span>
                                    </div>
                                  )}
                                  {(() => {
                                    const ownedItems =
                                      specialOffer.itemData?.filter(
                                        (item) =>
                                          "isOwned" in item &&
                                          item.isOwned === true,
                                      ) || [];

                                    if (ownedItems.length === 0) return null;

                                    return (
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <button
                                            type="button"
                                            className="hover:opacity-80 transition-opacity inline-flex"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <InfoIcon className="w-4 h-4 text-white/90" />
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent
                                          className="max-w-xs text-sm text-left"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <p className="font-semibold mb-2">
                                            {t("shop.ownedItemsInfo")}
                                          </p>
                                          <ul className="list-disc list-inside space-y-1">
                                            {ownedItems.map((item, idx) => (
                                              <li key={idx} className="text-xs">
                                                {translateShopSubItemName(
                                                  item.itemName,
                                                  item.itemId,
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                          <p className="mt-2 text-xs">
                                            {t("shop.priceDeductionNote")}
                                          </p>
                                        </PopoverContent>
                                      </Popover>
                                    );
                                  })()}
                                </div>
                              </div>
                            </button>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                    </Carousel>
                    {/* Indicator */}
                    {specialOffers.length > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-2">
                        {specialOffers.map((_, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => carouselApi?.scrollTo(index)}
                            className={cn(
                              "h-2 w-2 rounded-full transition-all cursor-pointer",
                              index === currentSlide
                                ? "bg-white"
                                : "bg-white/40",
                            )}
                            aria-label={`Go to slide ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Bundles Section */}
                {bundles.length > 0 && (
                  <div className="flex flex-col gap-2 w-full">
                    <div className="dark-blue-container w-full px-3 py-1 flex items-center justify-center">
                      <span
                        className="text-white font-black text-lg"
                        style={{
                          textShadow: generateTextShadow(1.2),
                        }}
                      >
                        {t("shop.bundles")}
                      </span>
                    </div>
                    {renderItemGrid(bundles)}
                  </div>
                )}

                {/* Consumables Section */}
                {consumables.length > 0 && (
                  <div className="flex flex-col gap-2 w-full">
                    <div className="dark-blue-container w-full px-3 py-1 flex items-center justify-center gap-2">
                      <span
                        className="text-white font-black text-lg"
                        style={{
                          textShadow: generateTextShadow(1.2),
                        }}
                      >
                        {t("shop.consumables")}
                      </span>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="hover:opacity-80 transition-opacity inline-flex"
                          >
                            <InfoIcon className="w-4 h-4 text-white/70" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="max-w-sm text-sm text-left">
                          <p>
                            Item you can consume. Boost Trophies and more soon!
                          </p>
                        </PopoverContent>
                      </Popover>
                    </div>
                    {renderItemGrid(consumables)}
                  </div>
                )}

                {/* Cosmetics Section */}
                {cosmetics.length > 0 && (
                  <div className="flex flex-col gap-2 w-full">
                    <div className="dark-blue-container w-full px-3 py-1 flex items-center justify-center gap-2">
                      <span
                        className="text-white font-black text-lg"
                        style={{
                          textShadow: generateTextShadow(1.2),
                        }}
                      >
                        {t("shop.skins.skinsTitle")}
                      </span>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="hover:opacity-80 transition-opacity inline-flex"
                          >
                            <InfoIcon className="w-4 h-4 text-white/70" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="max-w-sm text-sm text-left">
                          <p>{t("shop.skins.skinsDescription")}</p>
                        </PopoverContent>
                      </Popover>
                    </div>
                    {renderItemGrid(cosmetics)}
                  </div>
                )}

                {/* Show message if no items */}
                {shopItems.length === 0 && (
                  <div className="flex-1 flex items-center justify-center text-zinc-400 py-8">
                    {t("shop.noShopItemsAvailable")}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {selectedItem && (
          <ShopDialog
            item={selectedItem}
            isSheetOpen={isSheetOpen}
            onOpenChange={setIsSheetOpen}
            refetchShopList={refetchShopList}
          />
        )}

      </div>
    </Fragment>
  );
}
